import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { useLoginMutation } from "../features/auth/authApiSlice";
import { updateUserName } from "../store/modules/userData";
import { setCredentials } from "../store/slices/authSlice";
import { SuccessNotify, FailedNotify } from "../constants/tostify";
import { useSocialSignup } from "./auth/signup/use-social-signup";

export const useLoginState = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [login, { isLoading: isMutationLoading }] = useLoginMutation();
  const social = useSocialSignup();

  const [showpassword, setShowPassword] = useState("password");
  const [changeicon, setChangeicon] = useState(1);
  const [logindata, setLogindata] = useState({
    emailorphone: "",
    password: "",
    captchaToken: "",
  });
  const [captchaToken, setCaptchaToken] = useState("");
  const [suspendedError, setSuspendedError] = useState("");

  const [lockCooldown, setLockCooldown] = useState(() => {
    const savedLockUntil = localStorage.getItem("lockUntil");
    if (savedLockUntil) {
      const remaining = Math.ceil((parseInt(savedLockUntil, 10) - Date.now()) / 1000);
      return remaining > 0 ? remaining : 0;
    }
    return 0;
  });

  useEffect(() => {
    if (lockCooldown <= 0) {
      localStorage.removeItem("lockUntil");
      return;
    }
    const interval = setInterval(() => {
      setLockCooldown((prev) => {
        const nextVal = prev - 1;
        if (nextVal <= 0) {
          localStorage.removeItem("lockUntil");
          return 0;
        }
        return nextVal;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [lockCooldown]);

  const validationSchema = Yup.object().shape({
    emailorphone: Yup.string()
      .required("Email or Phone number is Required")
      .test(
        "email-or-phone",
        "Enter a valid email address or phone number",
        (value) => {
          if (!value) return false;
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const phoneRegex = /^\d{10}$/;
          return emailRegex.test(value) || phoneRegex.test(value);
        }
      ),
    password: Yup.string().required("Password is Required"),
    captchaToken: Yup.string().required("Please complete the reCAPTCHA"),
  });

  const userLoginadata = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!captchaToken) {
      FailedNotify("Please complete the reCAPTCHA to continue");
      return;
    }

    try {
      setSuspendedError("");
      const response = await login(logindata).unwrap();

      if (response?.success) {
        localStorage.removeItem("lockUntil");

        // Extract user and accessToken directly from the login response.
        // The backend now returns { success, data: { user, accessToken } }
        // so we don't need a separate /user/me call right after login.
        const user =
          response?.data?.user ||
          response?.data?.User ||
          response?.user ||
          null;

        const token = response?.data?.accessToken || null;
        const refreshToken = null; // stored in httpOnly cookie — not exposed to JS

        // Store credentials in Redux (token is needed by prepareHeaders for non-cookie requests)
        dispatch(setCredentials({ token, refreshToken, user }));

        // Keep user profile data in legacy user slice
        if (user) {
          dispatch(updateUserName(user));
        }

        SuccessNotify("Login Successfully");
        navigate("/BuyingSide");
      } else {
        if (response?.isSuspended || response?.message?.toLowerCase().includes("suspen")) {
          const msg = response?.message || "Your account is suspeneded ";
          setSuspendedError(msg);
          FailedNotify(msg);
        } else {
          FailedNotify(response?.message || "Login Failed");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error?.data?.timeLeft) {
        setLockCooldown(error.data.timeLeft);
        const lockUntilTimestamp = Date.now() + error.data.timeLeft * 1000;
        localStorage.setItem("lockUntil", lockUntilTimestamp.toString());
      }

      if (
        error?.data?.isSuspended ||
        error?.status === 403 ||
        error?.data?.message?.toLowerCase().includes("suspen")
      ) {
        const msg = error?.data?.message || "Your account is suspeneded ";
        setSuspendedError(msg);
        FailedNotify(msg);
      } else {
        FailedNotify(
          error?.data?.message || "Email or Password is Incorrect"
        );
      }
    }
  };

  const inputChange = (event) => {
    if (suspendedError) {
      setSuspendedError("");
    }
    setLogindata((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  };

  const setCaptchaValue = (value) => {
    const captchaValue = value || "";
    setCaptchaToken(captchaValue);
    setLogindata((prevState) => ({
      ...prevState,
      captchaToken: captchaValue,
    }));
  };

  return {
    navigate,
    dispatch,
    ...social,
    isLoading: isMutationLoading || social.isSocialLoading,
    showpassword,
    setShowPassword,
    changeicon,
    setChangeicon,
    logindata,
    setLogindata,
    captchaToken,
    setCaptchaToken: setCaptchaValue,
    validationSchema,
    userLoginadata,
    inputChange,
    lockCooldown,
    setLockCooldown,
    suspendedError,
    setSuspendedError,
  };
};
